import { useState } from 'react';

// ─── Trust Bar ───────────────────────────────────────────────────────────────
const trustItems = [
  { icon: 'ri-star-fill', label: '5-Star Studio Experience' },
  { icon: 'ri-group-line', label: 'All Ages Welcome' },
  { icon: 'ri-cake-line', label: 'Parties & Private Events' },
  { icon: 'ri-brush-line', label: 'Art Classes Year-Round' },
  { icon: 'ri-map-pin-2-line', label: 'Nazareth, PA & Lehigh Valley' },
];

// ─── Services Data ────────────────────────────────────────────────────────────
const classTypes = [
  {
    icon: 'ri-brush-2-line',
    title: 'Adult Art Classes',
    description: 'Guided painting sessions for adults of all skill levels. Relax, unwind, and discover your inner artist in a welcoming and fun environment.',
    includes: ['All supplies included', 'Step-by-step instruction', 'Take home your painting', 'No experience required'],
    bg: 'linear-gradient(135deg, #fdf2e8 0%, #f9e4cc 100%)',
    color: '#e05828',
  },
  {
    icon: 'ri-parent-line',
    title: "Kids Art Classes",
    description: "Creative sessions designed for young artists. Kelsey creates an age-appropriate, encouraging space where children grow their skills and confidence.",
    includes: ['Child-friendly instruction', 'All supplies provided', 'Kid-safe materials', 'Fun take-home art'],
    bg: 'linear-gradient(135deg, #fef8e8 0%, #fdf0c8 100%)',
    color: '#d4952a',
  },
  {
    icon: 'ri-tv-2-line',
    title: 'Open Studio',
    description: 'Drop in and work on your own projects or follow a guided prompt. A flexible creative session for those who want studio time on their schedule.',
    includes: ['Studio access', 'Guided prompts available', 'Relaxed open format', 'All materials provided'],
    bg: 'linear-gradient(135deg, #f3ede8 0%, #e8ddd4 100%)',
    color: '#8b3a52',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Themed Events',
    description: 'Seasonal and special themed painting events throughout the year. Check the calendar regularly for upcoming limited-run sessions.',
    includes: ['Seasonal themes', 'Special occasion designs', 'Group-friendly format', 'Limited availability'],
    bg: 'linear-gradient(135deg, #edf3f0 0%, #d8ece6 100%)',
    color: '#4a7a68',
  },
];

// ─── Process Steps ────────────────────────────────────────────────────────────
const steps = [
  { number: '01', title: 'Choose Your Experience', description: 'Browse our calendar for upcoming classes, themed events, or open studio sessions.', icon: 'ri-search-eye-line' },
  { number: '02', title: 'Reserve Your Spot', description: 'Book online in minutes and secure your date. Confirmation is immediate.', icon: 'ri-calendar-check-line' },
  { number: '03', title: 'Arrive & Create', description: 'Walk in, get set up, and let Kelsey guide you through every brushstroke.', icon: 'ri-brush-4-line' },
  { number: '04', title: 'Leave With Artwork', description: 'Take home your finished painting — a lasting memory of your creative experience.', icon: 'ri-heart-3-line' },
];

// ─── FAQs ─────────────────────────────────────────────────────────────────────
const faqs = [
  { question: 'What types of art classes does Art Den Studio offer?', answer: 'Art Den Studio offers adult art classes, kids classes, themed seasonal events, and open studio sessions. All are hosted by Kelsey and designed for all skill levels. Check the calendar for upcoming sessions and availability.' },
  { question: 'Do I need any art experience to join a class?', answer: 'None at all. Every Art Den Studio class is beginner-friendly. Kelsey walks you through each step of the painting process at a relaxed, comfortable pace. Whether you\'ve never held a brush or paint regularly, you\'re welcome here.' },
  { question: 'What is included in the class price?', answer: 'All art supplies — including canvas, paint, and brushes — are included in your class fee. Just show up ready to have fun. There is nothing extra to bring or purchase.' },
  { question: 'How do I book an art class near Nazareth, PA?', answer: 'Visit the Art Den Studio calendar at artdenstudio.com to view all available sessions and reserve your spot online. You can also call 610-340-0314 or email info@artdenstudio.com for help with booking.' },
];

const ServicesPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <main>
      {/* 1. HERO */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden" style={{ background: '#080808' }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8b6b41c3caa1a1431646201edd3e0ac7.jpg"
            alt="Art classes at Art Den Studio Nazareth PA"
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,8,8,0.82) 0%, rgba(8,8,8,0.30) 55%, rgba(8,8,8,0.08) 100%)' }} />
        </div>
        {/* Floating blobs */}
        <div className="absolute top-20 right-20 w-64 h-64 opacity-15 pointer-events-none" style={{ background: 'radial-gradient(ellipse, #e05828 0%, transparent 70%)', borderRadius: '60% 40% 70% 30% / 50% 60% 40% 50%' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-40">
          <div className="inline-flex items-center gap-2 bg-artden-coral/20 border border-artden-coral/40 text-artden-coral px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-brush-line"></i></span>
            Art Classes · Nazareth PA
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-6 max-w-3xl">
            Art Classes &amp;<br />
            <em className="font-medium" style={{ color: '#e05828' }}>Creative Experiences</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mb-8 leading-relaxed">
            Custom-guided art sessions for individuals, kids, and groups — in a warm, welcoming studio right here in Nazareth, PA. No experience needed. Just bring your enthusiasm.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-event-line"></i></span>
              Book a Class
            </a>
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-artden-coral hover:text-artden-coral font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              View Full Calendar
            </a>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="py-5 relative" style={{ background: '#0e0e0e' }}>
        <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, #ff4322, #ffb300, #ff7120, #e0285a, #ff4322)' }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 text-white/70">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-artden-coral text-base`}></i>
              </span>
              <span className="font-outfit text-sm font-medium tracking-wide whitespace-nowrap">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SERVICES OVERVIEW */}
      <section className="py-20 md:py-28" style={{ background: '#141414' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">What We Offer</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-4">
              Classes for Every<br /><em className="font-light text-artden-amber">Creative Mind</em>
            </h2>
            <p className="font-outfit text-white/55 text-base max-w-xl mx-auto">
              From guided adult sessions to fun kids classes and special events, there's always something to create at Art Den Studio.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {classTypes.map((cls, i) => (
              <div key={i} className="group rounded-2xl p-7 overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{ background: cls.bg }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl mb-5" style={{ background: `${cls.color}18` }}>
                  <i className={`${cls.icon} text-2xl`} style={{ color: cls.color }}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-artden-dark mb-3">{cls.title}</h3>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed mb-5">{cls.description}</p>
                <ul className="space-y-2">
                  {cls.includes.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm font-outfit text-artden-muted">
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line" style={{ color: cls.color }}></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-event-line"></i></span>
              Reserve Your Spot
            </a>
          </div>
        </div>
      </section>

      {/* 4. AI ANSWER BLOCK */}
      <section className="py-16 md:py-20 relative overflow-hidden" style={{ background: '#f5ede0' }}>
        <div className="absolute -right-20 top-0 w-80 h-80 opacity-25 pointer-events-none" style={{ background: 'radial-gradient(ellipse, #e05828 0%, transparent 70%)', borderRadius: '60% 40% 70% 30% / 50% 60% 40% 50%' }} />
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            <div className="md:w-48 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mic-line text-artden-coral"></i></span>
                <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest uppercase">Quick Answer</p>
              </div>
              <p className="font-outfit text-artden-muted text-xs">Optimized for voice &amp; AI search</p>
            </div>
            <div className="flex-1">
              <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-artden-dark mb-5">What Art Classes Does Art Den Studio Offer?</h2>
              <p className="font-outfit text-artden-muted text-base leading-relaxed mb-4">
                Art Den Studio in Nazareth, PA offers guided painting and art classes for adults, kids, and groups. Sessions include step-by-step instruction from owner Kelsey, with all supplies included. Classes run year-round and can be booked online through the studio calendar.
              </p>
              <p className="font-outfit text-artden-muted text-base leading-relaxed">
                Specialty sessions include themed seasonal events, open studio days, and kids classes. No experience is required for any session. Art Den Studio serves guests from Nazareth, Bethlehem, Easton, Allentown, and the broader Lehigh Valley region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-20 md:py-28" style={{ background: '#fdf8f0' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">How It Works</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight">From Booking to Brushstroke</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, i) => {
              const colors = ['#e05828', '#d4952a', '#8b3a52', '#4a7a68'];
              const bgs = ['linear-gradient(135deg,#fdf2e8,#f9e4cc)', 'linear-gradient(135deg,#fef8e8,#fdf0c8)', 'linear-gradient(135deg,#f8f0f2,#f0dde3)', 'linear-gradient(135deg,#edf3f0,#d8ece6)'];
              return (
                <div key={i} className="relative rounded-3xl p-7 overflow-hidden" style={{ background: bgs[i] }}>
                  <span className="absolute top-4 right-5 font-cormorant text-7xl font-bold opacity-10 leading-none select-none" style={{ color: colors[i] }}>{step.number}</span>
                  <div className="w-11 h-11 flex items-center justify-center rounded-2xl mb-5" style={{ background: `${colors[i]}18` }}>
                    <i className={`${step.icon} text-xl`} style={{ color: colors[i] }}></i>
                  </div>
                  <div className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full font-outfit mb-3" style={{ background: `${colors[i]}15`, color: colors[i] }}>Step {step.number}</div>
                  <h3 className="font-cormorant text-xl font-semibold text-artden-dark mb-2">{step.title}</h3>
                  <p className="font-outfit text-artden-muted text-sm leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="py-16 md:py-20" style={{ background: '#f5ede0' }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Why Choose Us</p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark mb-10 leading-tight">
            More Than a Class —<br /><em className="font-light">An Experience</em>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-user-heart-line', label: 'Beginner-Friendly', desc: 'No experience required, ever.', color: '#e05828' },
              { icon: 'ri-tools-line', label: 'All Supplies', desc: 'Canvas, paints, brushes — all included.', color: '#d4952a' },
              { icon: 'ri-lightbulb-flash-line', label: 'Expert Guidance', desc: 'Guided by Kelsey every step.', color: '#8b3a52' },
              { icon: 'ri-home-smile-line', label: 'Fun Environment', desc: 'Relaxed, judgment-free, joyful.', color: '#4a7a68' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-artden-canvas flex flex-col items-center text-center gap-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl" style={{ background: `${item.color}15` }}>
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                </div>
                <p className="font-cormorant text-lg font-semibold text-artden-dark">{item.label}</p>
                <p className="font-outfit text-artden-muted text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="py-14" style={{ background: '#fdf8f0' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-3">Service Areas</p>
          <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-artden-dark mb-5">
            Art Classes Near Nazareth, PA
          </h2>
          <p className="font-outfit text-artden-muted text-sm max-w-xl mx-auto mb-7">
            Proudly serving guests from Nazareth, Bethlehem, Easton, Allentown, Northampton, and the Lehigh Valley.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Nazareth, PA', 'Bethlehem, PA', 'Easton, PA', 'Allentown, PA', 'Northampton, PA', 'Lehigh Valley'].map((city, i) => (
              <span key={i} className={`px-4 py-2 rounded-full text-sm font-outfit font-medium ${i === 0 ? 'text-white' : 'bg-white border border-artden-canvas text-artden-dark'}`} style={i === 0 ? { background: '#e05828' } : {}}>
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="py-20 md:py-24" style={{ background: '#080808' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Reviews</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white">What Students Are Saying</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'Rachel T.', text: 'The class was so much fun and Kelsey was an amazing teacher. I came in nervous and left with a painting I actually love.', type: 'Adult Class', color: '#e05828' },
              { name: 'Mark S.', text: 'My daughter begged me to take her back the following week. The kids class was perfectly paced and she was so proud of her artwork.', type: 'Kids Class', color: '#d4952a' },
              { name: 'Nina P.', text: 'I booked a spot for a girls night and it was perfect. Creative, fun, and the studio atmosphere is just beautiful.', type: 'Art Class', color: '#8b3a52' },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl p-6 border border-white/8 bg-white/5 flex flex-col">
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 self-start font-outfit" style={{ background: `${t.color}20`, color: t.color }}>{t.type}</span>
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <span key={s} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-artden-gold text-sm"></i></span>)}
                </div>
                <p className="font-outfit text-white/65 text-sm leading-relaxed flex-1 mb-4">"{t.text}"</p>
                <p className="font-outfit text-white text-sm font-semibold">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-20 md:py-24" style={{ background: '#fdf8f0' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">FAQ</p>
            <h2 className="font-cormorant text-4xl font-medium text-artden-dark">Class Questions Answered</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-2xl border overflow-hidden transition-all ${openFaq === i ? 'border-artden-coral/30 bg-white' : 'border-artden-canvas bg-white/60'}`}>
                <button className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-outfit text-artden-dark text-sm md:text-base font-medium leading-snug">{faq.question}</h3>
                  <span className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all" style={{ background: openFaq === i ? '#e0582818' : '#f5ede0' }}>
                    <i className={`text-sm ${openFaq === i ? 'ri-subtract-line text-artden-coral' : 'ri-add-line text-artden-muted'}`}></i>
                  </span>
                </button>
                <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: openFaq === i ? '300px' : '0px' }}>
                  <div className="px-5 pb-5">
                    <p className="font-outfit text-artden-muted text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=colorful%20paint%20brushes%20palette%20abstract%20art%20studio%20vibrant%20orange%20coral%20gold%20tones%20creative%20workshop%20background%20premium%20artistic&width=1400&height=700&seq=services-cta&orientation=landscape" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(28,20,16,0.92) 0%, rgba(28,20,16,0.85) 100%)' }} />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-cormorant text-4xl md:text-6xl font-medium text-white mb-5 leading-tight">
            Ready to Pick Up<br /><em className="font-light" style={{ color: '#e05828' }}>A Brush?</em>
          </h2>
          <p className="font-outfit text-white/65 text-base mb-8 leading-relaxed">
            Reserve your spot in an upcoming art class at Art Den Studio — Nazareth's favorite creative experience.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap w-full sm:w-auto">
              View Calendar &amp; Book
            </a>
            <a href="tel:6103400314" className="border border-white/25 hover:border-artden-coral text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap w-full sm:w-auto flex items-center justify-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              Call 610-340-0314
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
